import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import IDCard from "@/components/IDCard";
import { BELT_NAMES } from "@/lib/belts";
import { IDCARD_TEMPLATES, mergeTemplates } from "@/lib/idcardTemplates";
import { X, Save, KeyRound, RefreshCcw, Camera, ShieldOff, LayoutTemplate } from "lucide-react";
import AttendanceStats from "@/components/AttendanceStats";
import PhotoCaptureModal from "@/components/PhotoCaptureModal";
import ApplyTemplateAllModal from "@/components/ApplyTemplateAllModal";
import { useT } from "@/context/LanguageContext";

/**
 * Slide-out editor for a single user. 4 tabs:
 *   Profile · Information · ID Card · Security
 *
 * Admins can edit role-permitted fields; super_admins can edit everything.
 */
const TAB_IDS = ["profile", "info", "idcard", "security"];

export default function UserDrawer({ user, currentUser, onClose, onSaved, initialTab = "profile" }) {
  const { t } = useT();
  const isSuper = currentUser?.role === "super_admin";
  const isAdminLike = ["admin", "super_admin"].includes(currentUser?.role);
  const [tab, setTab] = useState(initialTab);
  const [draft, setDraft] = useState(user);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [pw, setPw] = useState("");
  const [qrPng, setQrPng] = useState("");
  const [qrBusy, setQrBusy] = useState(false);
  const [liveTemplates, setLiveTemplates] = useState(() => mergeTemplates(null));
  const [photoModal, setPhotoModal] = useState(false);
  const [applyAllOpen, setApplyAllOpen] = useState(false);

  useEffect(() => { setDraft(user); }, [user]);

  useEffect(() => {
    let active = true;
    api.get("/idcard-templates")
      .then((r) => {
        if (!active) return;
        // Convert list → keyed object so the existing merge helper still works.
        const keyed = {};
        for (const t of r.data || []) {
          keyed[t.key] = { label: t.label, description: t.description, config: t.config || {} };
        }
        setLiveTemplates(mergeTemplates(keyed));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    // QR codes are always solid black for reliable scanning.
    api.get(`/users/${user.id}/qrcode`, { params: { color: "#000000" } })
      .then((r) => { if (active) setQrPng(r.data?.qr_png || ""); })
      .catch(() => {});
    return () => { active = false; };
  }, [user?.id, user?.qr_code]);

  if (!user) return null;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setOverride = (k, v) => setDraft((d) => ({
    ...d,
    idcard_overrides: { ...(d.idcard_overrides || {}), [k]: v },
  }));

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.2 * 1024 * 1024) { alert("Photo must be under 1.2 MB"); return; }
    const r = new FileReader();
    r.onload = () => set("photo_url", r.result);
    r.readAsDataURL(f);
  };

  const regenerateQR = async () => {
    if (!window.confirm("Generate a new QR code for this user? Their existing printed/screen card will stop working.")) return;
    setQrBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/users/${user.id}/qr/regenerate`);
      setDraft((d) => ({ ...d, qr_code: data.qr_code }));
      onSaved?.(data);
      setMsg("QR code regenerated.");
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setQrBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { id, member_number, created_at, ...payload } = draft;
      // Only send fields that changed (keep request small)
      const diff = {};
      for (const k of Object.keys(payload)) {
        if (JSON.stringify(payload[k]) !== JSON.stringify(user[k])) diff[k] = payload[k];
      }
      if (Object.keys(diff).length === 0) {
        setMsg("Nothing to save.");
      } else {
        const { data } = await api.patch(`/users/${user.id}`, diff);
        onSaved?.(data);
        setDraft(data);
        setMsg("Saved.");
      }
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (pw.length < 6) { setMsg("Password must be at least 6 chars."); return; }
    setBusy(true);
    setMsg("");
    try {
      await api.post(`/users/${user.id}/password`, { new_password: pw });
      setPw("");
      setMsg("Password updated.");
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetMfa = async () => {
    if (!window.confirm(`Reset two-factor for ${user.name}? Their authenticator and backup codes stop working, and they'll enrol a new app on next sign-in.`)) return;
    setBusy(true);
    setMsg("");
    try {
      await api.post(`/users/${user.id}/mfa/reset`);
      setDraft((d) => ({ ...d, mfa_enabled: false }));
      setMsg("Two-factor reset. The member will enrol again at next sign-in.");
    } catch (e) {
      setMsg(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="user-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full bg-[var(--dojo-paper)] border-l border-[var(--dojo-border)] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[var(--dojo-paper)] border-b border-[var(--dojo-border)] flex items-center justify-between px-6 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">{user.member_number}</div>
            <div className="font-serif text-2xl">{draft.name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[var(--dojo-hinomaru)]" data-testid="user-drawer-close"><X size={18} /></button>
        </div>
        <div className="flex gap-1 px-6 pt-4 border-b border-[var(--dojo-border)]">
          {TAB_IDS.map((tabId) => (
            <button
              key={tabId}
              onClick={() => setTab(tabId)}
              className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border-b-2 ${tab === tabId ? "border-[var(--dojo-green)] text-[var(--dojo-ink)]" : "border-transparent text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]"}`}
              data-testid={`user-tab-${tabId}`}
            >{t(`drawer.tab_${tabId}`)}</button>
          ))}
        </div>
        <div className="p-6 space-y-6">
          {/* PROFILE */}
          {tab === "profile" && (
            <div className="space-y-4 max-w-2xl">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full Name"><input className="input" value={draft.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" /></Field>
                <Field label="Email" hint="Optional — used for password resets and notifications."><input className="input" type="email" value={draft.email || ""} onChange={(e) => set("email", e.target.value)} disabled={!isSuper} placeholder="(optional)" data-testid="user-email-input" /></Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Username *" hint="Required. The login they'll type at sign-in.">
                  <input
                    className="input"
                    required
                    value={draft.username || ""}
                    onChange={(e) => set("username", e.target.value.replace(/\s/g, "").toLowerCase())}
                    placeholder="e.g. johnsmith"
                    disabled={!isAdminLike && currentUser?.id !== user.id}
                    data-testid="user-username-input"
                  />
                </Field>
                <Field label="Phone"><input className="input" value={draft.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Belt Rank">
                  <select className="input" value={draft.belt_rank || ""} onChange={(e) => set("belt_rank", e.target.value)} data-testid="user-belt-select">
                    <option value="">— None —</option>
                    {BELT_NAMES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
                {isSuper ? (
                  <Field label="Role">
                    <select className="input" value={draft.role} onChange={(e) => set("role", e.target.value)} data-testid="user-role-select">
                      <option value="student">Student</option>
                      <option value="team_member">Team Member</option>
                      <option value="sensei">Sensei</option>
                      <option value="renshi">Renshi</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </Field>
                ) : <div />}
              </div>
              <Field label="Active">
                <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={!!draft.active} onChange={(e) => set("active", e.target.checked)} data-testid="user-active-toggle" /> Account enabled</label>
              </Field>
              {isAdminLike && (
                <Field label="Auto-deactivation" hint="When enabled, this user is exempt from the dojo-wide inactivity sweep.">
                  <label className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={!!draft.deactivation_exempt}
                      onChange={(e) => set("deactivation_exempt", e.target.checked)}
                      data-testid="user-deactivation-exempt-toggle"
                    />
                    Exempt from auto-deactivation
                  </label>
                </Field>
              )}
              <Field label="Profile Photo" hint="Optional. Capture + crop with the camera, or pick a file.">
                <div className="flex items-center gap-3 flex-wrap">
                  {draft.photo_url && <img src={draft.photo_url} alt="" className="h-20 w-16 object-cover border border-[var(--dojo-border)]" />}
                  <button
                    type="button"
                    onClick={() => setPhotoModal(true)}
                    className="btn-outline flex items-center gap-2 text-xs"
                    data-testid="user-profile-photo-capture-btn"
                  ><Camera size={13} /> Capture / Crop</button>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--dojo-ink-soft)]">or</span>
                  <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="text-sm" data-testid="user-profile-photo-upload" />
                  {draft.photo_url && <button type="button" onClick={() => set("photo_url", "")} className="text-xs text-[var(--dojo-hinomaru)] underline">Remove</button>}
                </div>
              </Field>
            </div>
          )}

          {/* INFORMATION */}
          {tab === "info" && (
            <div className="space-y-4 max-w-2xl">
              <AttendanceStats userId={user.id} compact />
              <Field label="Date of Birth"><input className="input" type="date" value={draft.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
              <Field label="Address"><textarea className="input min-h-[60px]" value={draft.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Emergency Contact Name"><input className="input" value={draft.emergency_contact_name || ""} onChange={(e) => set("emergency_contact_name", e.target.value)} /></Field>
                <Field label="Emergency Contact Phone"><input className="input" value={draft.emergency_contact_phone || ""} onChange={(e) => set("emergency_contact_phone", e.target.value)} /></Field>
              </div>
              <Field label="Medical Notes"><textarea className="input min-h-[80px]" value={draft.medical_notes || ""} onChange={(e) => set("medical_notes", e.target.value)} placeholder="Allergies, injuries, accommodations…" /></Field>
              <Field label="Internal Notes" hint="Only staff can see this."><textarea className="input min-h-[80px]" value={draft.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
          )}

          {/* ID CARD */}
          {tab === "idcard" && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Field label="Template" hint="Pick a starting design. Saves instantly — per-user overrides stack on top.">
                  <select
                    className="input"
                    value={draft.idcard_template || ""}
                    onChange={async (e) => {
                      const newKey = e.target.value || null;
                      set("idcard_template", newKey);
                      // Auto-save the template selection so the user's actual
                      // card (and student dashboard) updates immediately —
                      // admins shouldn't need an extra "Save Changes" click
                      // just to switch templates. Per-user override fields
                      // still need the normal Save flow.
                      if (user?.id) {
                        try {
                          await api.patch(`/users/${user.id}`, { idcard_template: newKey });
                          if (onSaved) onSaved({ ...user, idcard_template: newKey });
                        } catch (err) { setMsg(formatApiError(err)); }
                      }
                    }}
                    data-testid="idcard-template-select"
                  >
                    <option value="">— Default (CMS) —</option>
                    {Object.entries(liveTemplates).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.description}</option>)}
                  </select>
                </Field>
                {isAdminLike && (
                  <button
                    type="button"
                    onClick={() => setApplyAllOpen(true)}
                    className="btn-outline flex items-center gap-2 text-xs w-full justify-center"
                    data-testid="idcard-apply-all-btn"
                  >
                    <LayoutTemplate className="w-4 h-4" /> Templates · Apply to selected members
                  </button>
                )}
                <div className="border border-[var(--dojo-border)] p-4 space-y-3">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Custom overrides (this user only)</div>
                  {/* Trimmed to only the elements that actually render on the
                      ID Card sample: photo/QR/background sizing, opacity and
                      position. Text/label/kanji overrides live on the
                      Template Editor now — this panel is for per-user tweaks. */}
                  <Field label="Issued Date" hint="Printed in the card footer. Leave empty to use the member's join date.">
                    <div className="flex items-center gap-3 flex-wrap">
                      <input
                        type="date"
                        value={(draft.idcard_overrides || {}).issued_date || ""}
                        onChange={(e) => setOverride("issued_date", e.target.value)}
                        className="input w-44"
                        data-testid="user-idcard-issued-date"
                      />
                      {(draft.idcard_overrides || {}).issued_date && (
                        <button
                          type="button"
                          onClick={() => setOverride("issued_date", "")}
                          className="ink-underline text-xs text-[var(--dojo-ink-soft)]"
                          data-testid="user-idcard-issued-date-clear"
                        >
                          Use join date
                        </button>
                      )}
                      <span className="text-[11px] text-[var(--dojo-ink-soft)]">
                        Join date: {draft.created_at ? new Date(draft.created_at).toLocaleDateString() : "—"}
                      </span>
                    </div>
                  </Field>

                  {/* Photo + QR + Background size sliders */}
                  <div className="border-t border-dashed border-[var(--dojo-border)] pt-3 mt-1 space-y-3" data-testid="user-idcard-sizes">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Photo, QR & Background Size</div>
                    {[
                      ["photo_size", "Member Photo", 100, 300],
                      ["qr_size", "QR Code", 100, 200],
                      ["background_size", "Card Background", 100, 200],
                    ].map(([key, label, , maxPct]) => {
                      const cur = (draft.idcard_overrides || {})[key];
                      const pct = Math.round(((cur ?? 1)) * 100);
                      return (
                        <div key={key} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                          <label className="text-[11px] text-[var(--dojo-ink-soft)]">{label}</label>
                          <input
                            type="range"
                            min="25"
                            max={maxPct}
                            step="5"
                            value={pct}
                            onChange={(e) => setOverride(key, Number(e.target.value) / 100)}
                            className="w-44 accent-[var(--dojo-green)]"
                            data-testid={`user-idcard-${key}-slider`}
                          />
                          <span className="font-mono-accent text-[11px] w-12 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] pt-1">Background opacity</div>
                    {(() => {
                      const cur = (draft.idcard_overrides || {}).background_opacity;
                      const pct = Math.round(((cur ?? 0.55)) * 100);
                      return (
                        <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                          <label className="text-[11px] text-[var(--dojo-ink-soft)]">Watermark opacity</label>
                          <input
                            type="range" min="20" max="100" step="5" value={pct}
                            onChange={(e) => setOverride("background_opacity", Number(e.target.value) / 100)}
                            className="w-44 accent-[var(--dojo-green)]"
                            data-testid="user-idcard-bg-opacity-slider"
                          />
                          <span className="font-mono-accent text-[11px] w-12 text-right">{pct}%</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Background position — the only nudge that survived the
                      trim, since the sample card leans heavily on the mountain
                      backdrop being aligned correctly. */}
                  <div className="border-t border-dashed border-[var(--dojo-border)] pt-3 mt-1 space-y-3" data-testid="user-idcard-offsets">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Background position (mm)</div>
                    {[
                      ["bg_offset_x", "Background — Left ↔ Right"],
                      ["bg_offset_y", "Background — Up ↕ Down"],
                    ].map(([key, label]) => {
                      const cur = Number((draft.idcard_overrides || {})[key] ?? 0);
                      const sliderVal = Math.max(-50, Math.min(50, cur));
                      return (
                        <div key={key} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                          <label className="text-[11px] text-[var(--dojo-ink-soft)]">{label}</label>
                          <input
                            type="range" min="-50" max="50" step="0.5" value={sliderVal}
                            onChange={(e) => setOverride(key, Number(e.target.value))}
                            className="w-44 accent-[var(--dojo-green)]"
                            data-testid={`user-idcard-${key}-slider`}
                          />
                          <input
                            type="number" min="-1500" max="1500" step="0.5" value={cur}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              if (!Number.isFinite(v)) return;
                              setOverride(key, Math.max(-1500, Math.min(1500, v)));
                            }}
                            className="input w-20 text-right font-mono-accent text-[11px]"
                            data-testid={`user-idcard-${key}-input`}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => set("idcard_overrides", {})}
                    className="text-xs text-[var(--dojo-hinomaru)] underline"
                    data-testid="idcard-clear-overrides"
                  >Clear all overrides</button>
                </div>

                {/* QR code management */}
                <div className="border border-[var(--dojo-border)] p-4 space-y-3">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">QR Code</div>
                  <div className="flex items-center gap-4">
                    {qrPng ? (
                      <img src={qrPng} alt="QR code" className="w-24 h-24 border border-[var(--dojo-border)]" data-testid="user-qr-preview" />
                    ) : (
                      <div className="w-24 h-24 border border-dashed border-[var(--dojo-border)] flex items-center justify-center text-[10px] text-[var(--dojo-ink-soft)]">Loading…</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Code</div>
                      <div className="font-mono-accent text-xs break-all" data-testid="user-qr-value">{draft.qr_code || "—"}</div>
                      <button
                        type="button"
                        onClick={regenerateQR}
                        disabled={qrBusy}
                        className="btn-outline mt-3 inline-flex items-center gap-2 text-xs"
                        data-testid="user-qr-regenerate"
                      >
                        <RefreshCcw size={12} className={qrBusy ? "animate-spin" : ""} />
                        {qrBusy ? "Rotating…" : "Regenerate QR"}
                      </button>
                    </div>
                  </div>
                  <div className="text-[10px] text-[var(--dojo-ink-soft)]">
                    Rotating the QR invalidates any previously printed cards for this user.
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-3">Live Preview · Horizontal</div>
                <IDCard user={draft} defaultOrientation="horizontal" />
                <div className="border border-[var(--dojo-border)] p-4 mt-4 space-y-2">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Member Photo on Card</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {draft.photo_url ? (
                      <img src={draft.photo_url} alt="" className="h-20 w-16 object-cover border border-[var(--dojo-border)]" data-testid="user-idcard-photo-preview" />
                    ) : (
                      <div className="h-20 w-16 border border-dashed border-[var(--dojo-border)] flex items-center justify-center text-[8px] uppercase tracking-[0.2em] text-[var(--dojo-ink-soft)] text-center px-1">No photo</div>
                    )}
                    <button
                      type="button"
                      onClick={() => setPhotoModal(true)}
                      className="btn-outline flex items-center gap-2 text-xs"
                      data-testid="user-idcard-photo-capture-btn"
                    ><Camera size={13} /> Capture / Crop</button>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--dojo-ink-soft)]">or</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={onPhoto}
                      className="text-sm"
                      data-testid="user-idcard-photo-upload"
                    />
                    {draft.photo_url && (
                      <button
                        type="button"
                        onClick={() => set("photo_url", "")}
                        className="text-xs text-[var(--dojo-hinomaru)] underline"
                      >Remove</button>
                    )}
                  </div>
                  <div className="text-[10px] text-[var(--dojo-ink-soft)]">
                    Photo appears on the card next to the QR. JPG/PNG under 1.2 MB.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECURITY */}
          {tab === "security" && (
            <div className="max-w-md space-y-5">
              <div className="border border-[var(--dojo-border)] p-4">
                <h3 className="font-serif text-lg mb-3">Reset Password</h3>
                <Field label="New Password"><input className="input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} placeholder="At least 6 characters" data-testid="user-newpass-input" /></Field>
                <button onClick={changePassword} disabled={busy || pw.length < 6} className="btn-primary mt-3 flex items-center gap-2" data-testid="user-newpass-submit">
                  <KeyRound size={14} /> {busy ? "Saving…" : "Update Password"}
                </button>
                <div className="text-xs text-[var(--dojo-ink-soft)] mt-2">User will need to use this on next login.</div>
              </div>

              <div className="border border-[var(--dojo-border)] p-4" data-testid="user-mfa-block">
                <h3 className="font-serif text-lg mb-1">Two-Factor Authentication</h3>
                <div className="text-xs mb-3">
                  Status:{" "}
                  <span className={draft.mfa_enabled ? "text-[var(--dojo-green)]" : "text-[var(--dojo-hinomaru)]"} data-testid="user-mfa-status">
                    {draft.mfa_enabled ? "Active — authenticator enrolled" : "Not enrolled"}
                  </span>
                </div>
                <button onClick={resetMfa} disabled={busy || !draft.mfa_enabled} className="btn-outline flex items-center gap-2" data-testid="user-mfa-reset-btn">
                  <ShieldOff size={14} /> {busy ? "Working…" : "Reset Two-Factor"}
                </button>
                <div className="text-xs text-[var(--dojo-ink-soft)] mt-2">
                  Use this when a member loses their phone. They will scan a new QR code the next time they sign in.
                </div>
              </div>
            </div>
          )}

          {msg && <div className="text-sm pt-3 border-t border-[var(--dojo-border)]" data-testid="user-drawer-msg">{msg}</div>}
        </div>
        {tab !== "security" && (
          <div className="sticky bottom-0 bg-[var(--dojo-paper)] border-t border-[var(--dojo-border)] px-6 py-4 flex justify-end gap-3">
            <button onClick={onClose} className="btn-outline">Close</button>
            <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="user-drawer-save">
              <Save size={14} /> {busy ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}
      </div>
      {applyAllOpen && (
        <ApplyTemplateAllModal
          previewUser={draft}
          onClose={() => setApplyAllOpen(false)}
          onApplied={(res) => {
            if (!(res.user_ids || []).includes(user.id)) {
              if (onSaved) onSaved({ ...user });  // refresh the list only
              return;
            }
            const next = {
              ...draft,
              idcard_template: res.key,
              idcard_overrides: res.cleared_overrides ? {} : draft.idcard_overrides,
            };
            setDraft(next);
            if (onSaved) onSaved({ ...user, idcard_template: res.key, idcard_overrides: next.idcard_overrides });
          }}
        />
      )}
      {photoModal && (
        <PhotoCaptureModal
          initialPhotoSize={(draft.idcard_overrides || {}).photo_size || 1}
          onClose={() => setPhotoModal(false)}
          onConfirm={(dataUrl, size) => {
            set("photo_url", dataUrl);
            if (size && size !== 1) setOverride("photo_size", size);
            setPhotoModal(false);
          }}
        />
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] block mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-[var(--dojo-ink-soft)] mt-1">{hint}</div>}
    </div>
  );
}
